import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarDays, ChevronLeft, ChevronRight, ChevronDown, Loader2, AlertTriangle, Film, MoonStar, Sparkles,
  RefreshCw, GraduationCap, Layers, Send, Plus, Library, X,
} from "lucide-react";
import { api, type BlackoutWindowView, type CalendarSlotView, type PostizPostView, type SocialPlatform } from "../../lib/api.js";
import { BlackoutConflictsDialog } from "./BlackoutConflictsDialog.js";
import { BackfillDialog, type BackfillPreset } from "./BackfillDialog.js";
import { ContentRadar } from "./ContentRadar.js";
import { PlannerDialog } from "./PlannerDialog.js";
import { MoveSlotDialog } from "./MoveSlotDialog.js";
import { VoiceLessonsDialog } from "./VoiceLessonsDialog.js";
import { ThinDecksDialog } from "./ThinDecksDialog.js";
import { ApproveDialog, splitForSending } from "./ApproveDialog.js";
import { ARTICLE_GAP_MINUTES } from "./calendarRules.js";

/**
 * What is going out, and when.
 *
 * WHY THIS PAGE EXISTS. The drip turns one article into eleven posts spread over
 * two weeks, and until this screen there was nowhere to see that. The Social tab
 * shows one article's slots as bare dates; the Distribution page reads the old
 * `distributions` send log and does not know `social_variants` exists; and the
 * Postiz calendar only holds what the drip has already handed over. So the
 * question "what goes out on Thursday" had no answer — and neither did "did two
 * articles land on the same slot".
 *
 * Times are Israel time throughout, because that is what the slots were chosen
 * in. Showing a UTC hour on this screen would make the whole slot table look
 * wrong for half the year.
 */

const TZ = "Asia/Jerusalem";

const PLATFORM_HE: Record<SocialPlatform, string> = {
  facebook: "פייסבוק", instagram: "אינסטגרם", tiktok: "טיקטוק", youtube: "יוטיוב",
};

/** One colour per platform, so a crowded day is legible at a glance. */
const PLATFORM_TINT: Record<SocialPlatform, string> = {
  facebook: "bg-sky-50 text-sky-900 border-sky-200",
  instagram: "bg-fuchsia-50 text-fuchsia-900 border-fuchsia-200",
  tiktok: "bg-slate-100 text-slate-900 border-slate-300",
  youtube: "bg-rose-50 text-rose-900 border-rose-200",
};

const FORMAT_HE: Record<string, string> = {
  feed_link: "פוסט+לינק", feed_image: "תמונה", carousel: "קרוסלה", reel: "ריל",
  story: "סטורי", video: "וידאו", photo_post: "תמונות", short: "שורט", long_video: "סרטון ארוך",
};

const dayKey = (iso: string) => new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(new Date(iso));
const hhmm = (iso: string) =>
  new Intl.DateTimeFormat("he-IL", { timeZone: TZ, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(new Date(iso));
const weekdayHe = (iso: string) =>
  new Intl.DateTimeFormat("he-IL", { timeZone: TZ, weekday: "short" }).format(new Date(iso));

/** Local midnight in Israel for a given offset in days from today. */
function israelDayStart(offsetDays: number): Date {
  const now = new Date();
  const key = new Intl.DateTimeFormat("en-CA", { timeZone: TZ }).format(now);
  const d = new Date(`${key}T00:00:00`);
  d.setDate(d.getDate() + offsetDays);
  return d;
}

/**
 * Postiz's states, kept as Postiz's states.
 *
 * `QUEUE` is scheduled and will go out; `DRAFT` is waiting for a person in
 * Postiz. Deliberately not mapped onto our variant statuses — they describe
 * different objects, and making a Postiz draft look like one of our unapproved
 * variants would invite acting on it here, where nothing can act on it.
 */
const POSTIZ_STATE_HE: Record<string, string> = {
  QUEUE: "מתוזמן",
  PUBLISHED: "פורסם",
  DRAFT: "טיוטה",
  ERROR: "נכשל",
};

const POSTIZ_STATE_TINT: Record<string, string> = {
  QUEUE: "bg-sky-100 text-sky-900",
  PUBLISHED: "bg-emerald-100 text-emerald-900",
  DRAFT: "bg-amber-100 text-amber-900",
  ERROR: "bg-rose-100 text-rose-900",
};

/**
 * Where a post stands, as the operator needs to read it off a card.
 *
 * `approved` alone does not say whether Postiz has it: a post approved here and
 * never sent sits in our database only. So the card state is derived from the
 * status AND `postizPostId` — anything Postiz holds is "scheduled", anything
 * approved that it does not is "approved, not sent".
 */
type CardState = "posted" | "scheduled" | "approved" | "draft" | "awaiting_media" | "failed";

function cardState(s: CalendarSlotView): CardState {
  if (s.status === "posted") return "posted";
  if (s.status === "scheduled" || s.postizPostId) return "scheduled";
  if (s.status === "approved") return "approved";
  if (s.status === "awaiting_media") return "awaiting_media";
  if (s.status === "failed") return "failed";
  return "draft";
}

const CARD_STATE: Record<CardState, { label: string; chip: string }> = {
  posted: { label: "✓ פורסם", chip: "bg-emerald-600 text-white" },
  scheduled: { label: "מתוזמן בפוסטיז", chip: "bg-sky-600 text-white" },
  approved: { label: "מאושר · לא נשלח", chip: "bg-amber-200 text-amber-950" },
  draft: { label: "טיוטה", chip: "bg-neutral-200 text-neutral-700" },
  awaiting_media: { label: "ממתין לווידאו", chip: "bg-violet-100 text-violet-900" },
  failed: { label: "נכשל", chip: "bg-rose-600 text-white" },
};

/**
 * What part of one Israel day is closed to posting, as the column prints it.
 * A window runs from the eve's cutoff to havdalah + 60 on its last rest day, so
 * a day is either the eve ("from 15:58"), the last day ("until 20:15"), a day
 * in the middle of a chain (all day), or both ends at once.
 */
function blockedPart(day: string, windows: BlackoutWindowView[]): { label: string; range: string; allDay: boolean } | null {
  for (const w of windows) {
    const startsHere = dayKey(w.startIso) === day;
    const endsHere = dayKey(w.endIso) === day;
    const inside = w.days.includes(day);
    if (!startsHere && !endsHere && !inside) continue;
    const range = startsHere && endsHere ? `${hhmm(w.startIso)}–${hhmm(w.endIso)}`
      : startsHere ? `מ־${hhmm(w.startIso)}`
      : endsHere ? `עד ${hhmm(w.endIso)}`
      : "כל היום";
    return { label: w.labelHe, range, allDay: inside && !endsHere };
  }
  return null;
}

const inBlackout = (iso: string, windows: BlackoutWindowView[]) =>
  windows.some((w) => Date.parse(w.startIso) <= Date.parse(iso) && Date.parse(iso) < Date.parse(w.endIso));

const LEGEND_ORDER: CardState[] = ["posted", "scheduled", "approved", "draft", "awaiting_media", "failed"];

export function CalendarPage() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [platform, setPlatform] = useState<SocialPlatform | "">("");
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  // How many edits are waiting to be learned from, on the button that learns.
  const voice = useQuery({ queryKey: ["social-voice"], queryFn: () => api.socialVoice(), staleTime: 60_000 });
  const pendingEdits = voice.data?.pendingEdits ?? 0;
  const proposedLessons = (voice.data?.lessons ?? []).filter((l) => l.status === "proposed").length;
  // The post being moved. Held by ID rather than by object so the dialog reads
  // the FRESH row after a refetch instead of a copy taken when it opened.
  const [movingId, setMovingId] = useState<number | null>(null);
  const [approveOpen, setApproveOpen] = useState(false);
  const [syncLine, setSyncLine] = useState<string | null>(null);
  const [blackoutOpen, setBlackoutOpen] = useState(false);
  const [backfillPreset, setBackfillPreset] = useState<BackfillPreset | undefined>(undefined);
  const [plannerOpen, setPlannerOpen] = useState(false);
  const [thinOpen, setThinOpen] = useState(false);
  // Counted on the button, so a thin post is visible before anyone opens it.
  const thin = useQuery({ queryKey: ["thin-decks"], queryFn: () => api.thinDecks(), staleTime: 60_000 });
  const thinCount = thin.data?.rows.length ?? 0;
  // How many of the planner's picks wait for a decision, on the button itself.
  const planner = useQuery({ queryKey: ["social-planner"], queryFn: () => api.socialPlanner(), staleTime: 60_000 });
  const pendingPicks = (planner.data?.proposal?.picks ?? []).filter((p) => p.decision === "pending").length;
  // Posts already scheduled inside Shabbat / chag / memorial days, 60 days out.
  const conflicts = useQuery({ queryKey: ["blackout-conflicts"], queryFn: () => api.blackoutConflicts(60), staleTime: 60_000 });
  const conflictCount = conflicts.data?.conflicts.length ?? 0;
  const reviewCount = conflicts.data?.reviews.length ?? 0;

  const sync = useMutation({
    mutationFn: () => api.reconcilePostiz(),
    onSuccess: (r) => {
      if (r.error) { setSyncLine(`לא הצלחתי לקרוא את פוסטיז, לא שונה כלום: ${r.error}`); return; }
      const parts = [
        r.published && `${r.published} פורסמו`,
        r.datesAdopted && `${r.datesAdopted} תאריכים עודכנו`,
        r.textAdopted && `${r.textAdopted} טקסטים עודכנו`,
        r.errored && `${r.errored} נכשלו`,
        r.deleted && `${r.deleted} נמחקו בפוסטיז`,
      ].filter(Boolean);
      setSyncLine(parts.length ? `סונכרן: ${parts.join(" · ")}` : `סונכרן: אין שינויים (${r.checked} נבדקו)`);
      void calendar.refetch();
    },
    onError: (e) => setSyncLine((e as Error).message),
  });

  // A fortnight per view: the drip is a 14-day shape, so a 7-day window cuts
  // every article in half and a month is too dense to read.
  const from = useMemo(() => israelDayStart(weekOffset * 14), [weekOffset]);
  const to = useMemo(() => israelDayStart(weekOffset * 14 + 14), [weekOffset]);

  // "24.9 – 7.10" — the fortnight on screen, which is also the "back to today" button.
  const rangeLabel = useMemo(() => {
    const f = new Intl.DateTimeFormat("he-IL", { timeZone: "Asia/Jerusalem", day: "numeric", month: "numeric" });
    return `${f.format(from)} – ${f.format(new Date(to.getTime() - 1))}`;
  }, [from, to]);

  const calendar = useQuery({
    queryKey: ["social-calendar", from.toISOString(), to.toISOString(), platform],
    queryFn: () => api.socialCalendar({
      from: from.toISOString(), to: to.toISOString(), ...(platform ? { platform } : {}),
    }),
  });

  const slots = calendar.data?.slots ?? [];
  const blackouts = calendar.data?.blackouts ?? [];
  const hebrewByDay = useMemo(() => new Map((calendar.data?.days ?? []).map((d) => [d.date, d])), [calendar.data?.days]);

  const byDay = useMemo(() => {
    const map = new Map<string, CalendarSlotView[]>();
    for (let i = 0; i < 14; i++) {
      const d = new Date(from);
      d.setDate(d.getDate() + i);
      map.set(dayKey(d.toISOString()), []);
    }
    for (const s of slots) {
      const k = dayKey(s.scheduledFor);
      if (map.has(k)) map.get(k)!.push(s);
    }
    for (const arr of map.values()) arr.sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor));
    return map;
  }, [slots, from]);

  // Two DIFFERENT articles within three hours of each other — on any platform —
  // is what the fill planner never produces. Moving a post by hand can still
  // create it, so the calendar rings it. Posts of ONE article sharing a moment
  // are the plan, not a collision.
  const collisions = useMemo(() => {
    const out = new Set<number>();
    for (let i = 0; i < slots.length; i++) {
      for (let j = i + 1; j < slots.length; j++) {
        const a = slots[i]!, b = slots[j]!;
        if (a.packId === b.packId) continue;
        if (Math.abs(Date.parse(a.scheduledFor) - Date.parse(b.scheduledFor)) / 60000 < ARTICLE_GAP_MINUTES) {
          out.add(a.id); out.add(b.id);
        }
      }
    }
    return out;
  }, [slots]);

  const awaiting = slots.filter((s) => s.status === "awaiting_media").length;

  // Sendable RIGHT NOW, counted by the same function the dialog acts on — so the
  // number on the button is never one the dialog then refuses. Includes posts
  // approved earlier that never reached Postiz.
  const pendingApproval = useMemo(() => splitForSending(slots, Date.now(), blackouts).ready.length, [slots, blackouts]);

  // Where each post stands, counted for the legend. The cards used to print a
  // status only for the unusual ones, so a draft, an approved post that never
  // left, and one Postiz holds all looked the same.
  const statusCounts = useMemo(() => {
    const c: Record<CardState, number> = { posted: 0, scheduled: 0, approved: 0, draft: 0, awaiting_media: 0, failed: 0 };
    for (const s of slots) c[cardState(s)]++;
    return c;
  }, [slots]);

  // Postiz's own calendar, for the same window. Anything written directly in
  // Postiz — or posted before this platform could see it — used to be invisible
  // here, and two calendars for one publishing schedule is how a slot gets
  // double-booked. Ours are filtered out server-side, so everything in this list
  // is something the platform did not create.
  const postiz = calendar.data?.postiz;
  const postizPosts = postiz?.posts ?? [];

  /**
   * Postiz-only posts, IN THE DAY THEY GO OUT ON.
   *
   * They used to be a flat strip above the grid, sorted by date and nowhere near
   * the day they belong to. That answers "does Postiz hold anything we don't"
   * and not "what goes out on Sunday", which is the question this page exists
   * for — an operator comparing the two calendars day by day was reading two
   * different layouts and concluding the schedules disagreed (Dor, 2026-09-16).
   *
   * Anything with no date, or a date outside the fortnight on screen, falls back
   * to the strip: a post that cannot be placed in a column must not vanish.
   */
  const postizByDay = useMemo(() => {
    const map = new Map<string, PostizPostView[]>();
    const loose: PostizPostView[] = [];
    for (const p of postizPosts) {
      const k = p.publishDate ? dayKey(p.publishDate) : null;
      if (!k || !byDay.has(k)) { loose.push(p); continue; }
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(p);
    }
    for (const arr of map.values()) arr.sort((a, b) => (a.publishDate ?? "").localeCompare(b.publishDate ?? ""));
    return { map, loose };
  }, [postizPosts, byDay]);

  return (
    <div className="space-y-4 p-4">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <CalendarDays size={18} /> יומן פרסום
        </h1>

        <div className="flex items-center rounded-lg border border-line bg-white">
          <button onClick={() => setWeekOffset((w) => w - 1)} className="rounded-s-lg p-1.5 hover:bg-canvas" title="שבועיים אחורה" aria-label="שבועיים אחורה">
            <ChevronRight size={15} />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            disabled={weekOffset === 0}
            title="חזרה להיום"
            className="border-x border-line px-2.5 py-1 text-xs tabular-nums hover:bg-canvas disabled:cursor-default disabled:hover:bg-transparent"
          >
            {rangeLabel}
          </button>
          <button onClick={() => setWeekOffset((w) => w + 1)} className="rounded-e-lg p-1.5 hover:bg-canvas" title="שבועיים קדימה" aria-label="שבועיים קדימה">
            <ChevronLeft size={15} />
          </button>
        </div>

        <select
          value={platform}
          onChange={(e) => setPlatform(e.target.value as SocialPlatform | "")}
          aria-label="סינון לפי פלטפורמה"
          className="rounded-lg border border-line bg-white px-2 py-1.5 text-xs"
        >
          <option value="">כל הפלטפורמות</option>
          {(Object.keys(PLATFORM_HE) as SocialPlatform[]).map((p) => (
            <option key={p} value={p}>{PLATFORM_HE[p]}</option>
          ))}
        </select>

        <span className="grow" />

        {/* Secondary tools as icons; each says what it does on hover, and a
            count rides on the icon so something waiting is still visible. */}
        <div className="flex items-center gap-0.5 rounded-lg border border-line bg-white p-0.5">
          {/* Read Postiz back now. The calendar already does it on load, at
              most once a minute; this is for "I just changed something in Postiz". */}
          <ToolButton
            label="רענון מפוסטיז"
            hint="מושך מפוסטיז הזזות, עריכות, פרסומים ומחיקות. לא כותב לפוסטיז כלום."
            onClick={() => sync.mutate()}
            disabled={sync.isPending}
          >
            <RefreshCw size={16} className={sync.isPending ? "animate-spin" : ""} />
          </ToolButton>
          <ToolButton
            label="למידה מעריכות"
            hint={proposedLessons > 0 ? `${proposedLessons} כללים ממתינים לאישור` : pendingEdits > 0 ? `${pendingEdits} עריכות חדשות ללמוד מהן` : "כללי כתיבה שנלמדו מהעריכות שלכם"}
            onClick={() => setVoiceOpen(true)}
            badge={proposedLessons > 0 ? proposedLessons : pendingEdits}
            badgeTone={proposedLessons > 0 ? "brand" : "muted"}
          >
            <GraduationCap size={16} />
          </ToolButton>
          <ToolButton
            label="פוסטים רזים"
            hint="קרוסלות וסטוריז עם פחות מ־4 שקופיות תוכן, או פחות ממה שהכיתוב מבטיח"
            onClick={() => setThinOpen(true)}
            badge={thinCount}
            badgeTone="warn"
          >
            <Layers size={16} />
          </ToolButton>
        </div>

        {/* Two ways to put posts on the calendar: the planner decides WHICH
            articles and WHEN; the fill is the same machinery driven by hand. */}
        <AddPostsMenu
          pendingPicks={pendingPicks}
          onPlanner={() => setPlannerOpen(true)}
          onBackfill={() => setBackfillOpen(true)}
        />

        {/* The gate, where the posts are. Counted on the button so an operator
            can see there is something waiting without opening it. */}
        <button
          onClick={() => setApproveOpen(true)}
          disabled={pendingApproval === 0}
          title={pendingApproval === 0 ? "אין פוסטים שממתינים לאישור בטווח הזה" : "אישור ושליחה לפוסטיז"}
          className="flex items-center gap-1.5 rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800 disabled:bg-neutral-300"
        >
          <Send size={14} /> אישור ושליחה
          {pendingApproval > 0 && (
            <span className="rounded-full bg-white/25 px-1.5 text-[11px] tabular-nums">{pendingApproval}</span>
          )}
        </button>
      </header>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted">
        <span>
          {slots.length} פוסטים שלנו בטווח
          {postizPosts.length > 0 ? ` · ועוד ${postizPosts.length} בפוסטיז בלבד` : ""} · שעון ישראל
        </span>
        {slots.length > 0 && (
          <span className="flex flex-wrap items-center gap-1">
            {LEGEND_ORDER.filter((k) => statusCounts[k] > 0).map((k) => (
              <span key={k} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${CARD_STATE[k].chip}`}>
                {CARD_STATE[k].label} {statusCounts[k]}
              </span>
            ))}
          </span>
        )}
        {awaiting > 0 && (
          <span className="flex items-center gap-1 text-sky-800">
            <Film size={13} /> {awaiting} ממתינים לווידאו ולא ניתנים לשליחה
          </span>
        )}
        {syncLine && (
          <span className="flex items-center gap-1">
            <RefreshCw size={12} /> {syncLine}
            <button onClick={() => setSyncLine(null)} className="ms-1 opacity-60 hover:opacity-100" aria-label="הסתרה">
              <X size={12} />
            </button>
          </span>
        )}
        {collisions.size > 0 && (
          <span className="flex items-center gap-1 text-amber-800">
            <AlertTriangle size={13} /> {collisions.size} פוסטים של מאמרים שונים בפחות מ־3 שעות זה מזה
          </span>
        )}
      </div>

      {/* Reported rather than hidden: a calendar that silently shows only half
          the schedule is worse than one that says which half is missing. */}
      {postiz?.error && (
        <p className="rounded-lg border border-warn bg-warn/5 px-3 py-2 text-xs text-amber-900">
          לא הצלחתי לקרוא את היומן של פוסטיז — מוצגים רק הפוסטים של הפלטפורמה. {postiz.error}
        </p>
      )}

      {/* ONLY what could not be placed in a column: a post with no date, or one
          whose date falls outside the fortnight on screen. Everything datable is
          drawn in its own day now, beside our posts, which is where the two
          calendars actually get compared. */}
      {postizByDay.loose.length > 0 && (
        <section className="rounded-lg border border-line bg-canvas-soft p-3">
          <h2 className="mb-2 text-xs font-semibold">
            בפוסטיז ולא אצלנו, בלי תאריך בטווח · {postizByDay.loose.length}
            <span className="ms-2 font-normal text-muted">
              נוצרו ישירות בפוסטיז, או לפני שהיומן הזה ידע לקרוא משם
            </span>
          </h2>
          <ul className="space-y-1">
            {postizByDay.loose
              .slice()
              .sort((a, b) => (a.publishDate ?? "").localeCompare(b.publishDate ?? ""))
              .map((p) => (
                <li key={p.id} className="flex flex-wrap items-baseline gap-2 text-[11px]">
                  <span className={`rounded px-1.5 py-0.5 font-medium ${POSTIZ_STATE_TINT[p.state] ?? "bg-neutral-200 text-neutral-700"}`}>
                    {POSTIZ_STATE_HE[p.state] ?? p.state}
                  </span>
                  <span className="text-muted">
                    {p.publishDate ? new Date(p.publishDate).toLocaleString("he-IL", { timeZone: "Asia/Jerusalem", dateStyle: "short", timeStyle: "short" }) : "—"}
                  </span>
                  {p.channel && <span className="text-muted">· {p.channel}</span>}
                  <span dir="auto" className="flex-1 truncate">{p.content}</span>
                  {p.releaseUrl && (
                    <a href={p.releaseUrl} target="_blank" rel="noreferrer" className="text-brand-700 underline">
                      לפוסט
                    </a>
                  )}
                </li>
              ))}
          </ul>
        </section>
      )}

      <ContentRadar onReshare={(p) => { setBackfillPreset(p); setBackfillOpen(true); }} />

      {conflictCount === 0 && reviewCount > 0 && (
        <button
          type="button"
          onClick={() => setBlackoutOpen(true)}
          className="flex w-full items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-right text-xs text-amber-900 hover:bg-amber-100"
        >
          <MoonStar size={14} />
          <strong>{reviewCount} פוסטים לבדיקה</strong>
          <span>(טקסט שמזכיר חג בתאריך אחר, או פוסט בערב יום זיכרון)</span>
          <span className="ms-auto underline">לצפייה</span>
        </button>
      )}

      {conflictCount > 0 && (
        <button
          type="button"
          onClick={() => setBlackoutOpen(true)}
          className="flex w-full items-center gap-2 rounded-lg border border-danger bg-rose-50 px-3 py-2 text-right text-xs text-rose-900 hover:bg-rose-100"
        >
          <MoonStar size={14} />
          <strong>{conflictCount} פוסטים מתוזמנים בזמן חסום</strong>
          <span>(שבת, חג או יום זיכרון, ב-60 הימים הקרובים){reviewCount > 0 ? ` · ועוד ${reviewCount} לבדיקה` : ""}</span>
          <span className="ms-auto underline">לבדיקה והעברה</span>
        </button>
      )}

      {calendar.isPending && <p className="flex items-center gap-2 text-sm text-muted"><Loader2 size={14} className="animate-spin" /> טוען…</p>}
      {calendar.isError && <p className="text-sm text-danger">{(calendar.error as Error).message}</p>}

      {calendar.data && slots.length === 0 && (
        <div className="rounded-lg border border-line bg-canvas p-4 text-sm text-muted">
          {postizPosts.length > 0
            ? "אין פוסטים של הפלטפורמה מתוזמנים בטווח הזה — מה שמופיע ביומן נוצר ישירות בפוסטיז. אפשר למלא את היומן ממאמרים קיימים בכפתור למעלה."
            : "אין פוסטים מתוזמנים בטווח הזה. אם יש מאמרים שכבר פורסמו, אפשר למלא את היומן מהם בכפתור למעלה."}
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-7">
        {[...byDay.entries()].map(([day, items]) => {
          // Closed stretches come from the real Hebrew calendar (server-side),
          // not from the weekday: Shabbat starts on Friday afternoon, ends on
          // Saturday evening, and a chag can land on any day.
          const blocked = blockedPart(day, blackouts);
          const heb = hebrewByDay.get(day);
          return (
            <div key={day} className={`rounded-lg border p-2 ${blocked?.allDay ? "border-dashed border-line bg-canvas/50" : "border-line"}`}>
              <div className="mb-1 flex items-baseline justify-between">
                <span className="text-xs font-medium">{weekdayHe(`${day}T12:00:00`)}</span>
                <span className="text-[11px] text-muted">{day.slice(5)}</span>
              </div>
              {heb && (
                <div className="mb-1 text-[10px] leading-tight text-muted">
                  {heb.hebrewDate}
                  {heb.holidays.length > 0 && <span className="block font-medium text-ink">{heb.holidays.join(" · ")}</span>}
                </div>
              )}
              {blocked && (
                <p className="mb-1.5 flex items-center gap-1 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] text-neutral-700" title="לא מפרסמים משעתיים לפני הדלקת נרות ועד שעה אחרי צאת השבת/החג">
                  <MoonStar size={10} /> חסום {blocked.range}
                </p>
              )}
              {items.length === 0 && (postizByDay.map.get(day)?.length ?? 0) === 0 && !blocked && (
                <p className="text-[11px] text-muted">—</p>
              )}
              <div className="space-y-1">
                {items.map((s) => (
                  // A BUTTON, not a div. The card has always carried everything
                  // needed to act on a post and offered nothing to press; the
                  // hour it shows was decided by a table in the source and could
                  // not be argued with from here.
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setMovingId(s.id)}
                    title={`${s.label ?? s.campaign}\n${s.hook}\n\nלחיצה — העברה לזמן אחר`}
                    className={`w-full rounded border px-1.5 py-1 text-right text-[11px] ${PLATFORM_TINT[s.platform]} ${
                      inBlackout(s.scheduledFor, blackouts) && s.status !== "posted" ? "ring-2 ring-danger"
                        : collisions.has(s.id) ? "ring-1 ring-amber-500" : ""
                    } ${s.status === "posted" ? "cursor-default" : "hover:brightness-95"}`}
                  >
                    {/* The PLATFORM is named, not just tinted. A card reading
                        "12:30 · סטורי" is ambiguous between Facebook and
                        Instagram, and colour alone fails anyone who does not
                        already know the palette. */}
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-medium">{hhmm(s.scheduledFor)}</span>
                      <span className="truncate opacity-75">
                        {PLATFORM_HE[s.platform]} · {FORMAT_HE[s.format] ?? s.format}
                        {/* The frame count, because a 4-frame story and an
                            8-slide carousel look the same everywhere else. */}
                        {s.slideCount > 1 ? ` · ${s.slideCount}` : ""}
                      </span>
                    </div>
                    <div className="truncate opacity-90">{s.label ?? s.campaign}</div>
                    {/* EVERY card says where it stands. Printing a status only
                        for the unusual ones made a draft, an approved post that
                        never left, and a post Postiz holds look identical. */}
                    <div className="mt-0.5">
                      <span className={`inline-block rounded px-1 py-px text-[10px] font-medium ${CARD_STATE[cardState(s)].chip}`}>
                        {CARD_STATE[cardState(s)].label}
                      </span>
                    </div>
                    {/* Only when it is NEWS. A variant awaiting its video has no
                        media by definition, and printing both lines on every one
                        of them buried the cards that are genuinely broken —
                        a post that is approved and has nothing to post. */}
                    {inBlackout(s.scheduledFor, blackouts) && s.status !== "posted" && (
                      <div className="font-medium text-danger">בזמן חסום</div>
                    )}
                    {s.deck?.thin && s.status !== "posted" && (
                      <div className="font-medium text-amber-800">רזה · {s.deck.content} שקופיות תוכן</div>
                    )}
                    {!s.hasMedia && s.status !== "awaiting_media" && (
                      <div className="font-medium text-amber-800">אין מדיה</div>
                    )}
                  </button>
                ))}

                {/* Postiz's own, in the day they go out on. Deliberately drawn
                    DIFFERENTLY — dashed, uncoloured, not a button: it has no
                    pack, no checks and no approval history here, and nothing on
                    this page can act on it. Making it look like one of ours
                    would invite pressing it. */}
                {(postizByDay.map.get(day) ?? []).map((p) => (
                  <div
                    key={p.id}
                    title={`${p.channel ?? ""}\n${p.content}\n\nנוצר בפוסטיז — לעריכה פותחים אותו שם`}
                    className="w-full rounded border border-dashed border-neutral-400 bg-canvas px-1.5 py-1 text-right text-[11px] text-muted"
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="font-medium">{p.publishDate ? hhmm(p.publishDate) : "—"}</span>
                      <span className="truncate">{p.channel ?? p.provider ?? "פוסטיז"}</span>
                    </div>
                    <div dir="auto" className="truncate">{p.content}</div>
                    <div className="mt-0.5 flex items-center gap-1">
                      <span className={`inline-block rounded px-1 py-px text-[10px] font-medium ${POSTIZ_STATE_TINT[p.state] ?? "bg-neutral-200 text-neutral-700"}`}>
                        {POSTIZ_STATE_HE[p.state] ?? p.state}
                      </span>
                      <span className="text-[10px]">בפוסטיז בלבד</span>
                      {p.releaseUrl && (
                        <a href={p.releaseUrl} target="_blank" rel="noreferrer" className="text-[10px] text-brand-700 underline">
                          לפוסט
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {movingId !== null && (() => {
        const slot = slots.find((s) => s.id === movingId);
        if (!slot) return null;
        return (
          <MoveSlotDialog
            slot={slot}
            siblings={slots}
            blackouts={blackouts}
            postizAppUrl={calendar.data?.postiz.appUrl ?? null}
            onClose={() => setMovingId(null)}
            onMoved={() => { setMovingId(null); void calendar.refetch(); }}
            onRefresh={() => { void calendar.refetch(); }}
          />
        );
      })()}

      {approveOpen && (
        <ApproveDialog
          slots={slots}
          blackouts={blackouts}
          onClose={() => setApproveOpen(false)}
          onDone={() => { setApproveOpen(false); void calendar.refetch(); }}
        />
      )}

      {plannerOpen && (
        <PlannerDialog
          onClose={() => { setPlannerOpen(false); void planner.refetch(); }}
          onDone={() => { setPlannerOpen(false); void planner.refetch(); void calendar.refetch(); void conflicts.refetch(); }}
        />
      )}

      {blackoutOpen && (
        <BlackoutConflictsDialog
          onClose={() => setBlackoutOpen(false)}
          onDone={() => { setBlackoutOpen(false); void conflicts.refetch(); void calendar.refetch(); }}
        />
      )}

      {thinOpen && (
        <ThinDecksDialog
          onClose={() => setThinOpen(false)}
          onChanged={() => { void calendar.refetch(); void thin.refetch(); }}
        />
      )}
      {voiceOpen && <VoiceLessonsDialog onClose={() => { setVoiceOpen(false); void voice.refetch(); }} />}

      {backfillOpen && (
        <BackfillDialog
          {...(backfillPreset ? { preset: backfillPreset } : {})}
          onClose={() => { setBackfillOpen(false); setBackfillPreset(undefined); }}
          onDone={() => { setBackfillOpen(false); setBackfillPreset(undefined); void calendar.refetch(); }}
        />
      )}
    </div>
  );
}

/** An icon button in the header toolbar: a hover label, and a count when something waits. */
function ToolButton({
  label, hint, onClick, disabled, badge = 0, badgeTone = "muted", children,
}: {
  label: string;
  hint?: string;
  onClick: () => void;
  disabled?: boolean;
  badge?: number;
  badgeTone?: "muted" | "brand" | "warn";
  children: ReactNode;
}) {
  const tone = { muted: "bg-neutral-500", brand: "bg-brand-700", warn: "bg-amber-500" }[badgeTone];
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="group relative rounded-md p-1.5 text-muted hover:bg-canvas hover:text-ink disabled:opacity-50"
    >
      {children}
      {badge > 0 && (
        <span className={`absolute -end-1 -top-1 min-w-4 rounded-full px-1 text-center text-[10px] font-medium leading-4 text-white ${tone}`}>
          {badge}
        </span>
      )}
      <span
        role="tooltip"
        className="pointer-events-none absolute end-0 top-full z-40 mt-1.5 hidden w-max max-w-56 rounded-md bg-neutral-900 px-2 py-1 text-start text-[11px] leading-snug text-white shadow group-hover:block group-focus-visible:block"
      >
        <strong className="block font-medium">{label}</strong>
        {hint && <span className="block opacity-80">{hint}</span>}
      </span>
    </button>
  );
}

/** "הוספת פוסטים": the planner and the manual fill, behind one button. */
function AddPostsMenu({
  pendingPicks, onPlanner, onBackfill,
}: {
  pendingPicks: number;
  onPlanner: () => void;
  onBackfill: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("keydown", onKey); };
  }, [open]);
  const pick = (fn: () => void) => () => { setOpen(false); fn(); };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-1.5 rounded-lg border border-brand-700 px-3 py-1.5 text-sm font-medium text-brand-700 hover:bg-brand-50"
      >
        <Plus size={14} /> הוספת פוסטים
        {pendingPicks > 0 && <span className="rounded-full bg-brand-700 px-1.5 text-[10px] text-white">{pendingPicks}</span>}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div role="menu" className="absolute end-0 top-full z-40 mt-1 w-72 overflow-hidden rounded-lg border border-line bg-white py-1 shadow-lg">
          <MenuItem icon={<Sparkles size={15} />} title="תכנון אוטומטי" onClick={pick(onPlanner)}
            text="המערכת בוחרת אילו מאמרים ומתי, ואתם מאשרים" badge={pendingPicks} />
          <MenuItem icon={<Library size={15} />} title="מילוי ממאמרים קיימים" onClick={pick(onBackfill)}
            text="בוחרים בעצמכם מאמרים ודפי טיפול ישנים לשיתוף" />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon, title, text, badge = 0, onClick }: { icon: ReactNode; title: string; text: string; badge?: number; onClick: () => void }) {
  return (
    <button role="menuitem" onClick={onClick} className="flex w-full items-start gap-2.5 px-3 py-2 text-start hover:bg-canvas">
      <span className="mt-0.5 text-brand-700">{icon}</span>
      <span className="min-w-0 grow">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          {title}
          {badge > 0 && <span className="rounded-full bg-brand-700 px-1.5 text-[10px] text-white">{badge} ממתינות</span>}
        </span>
        <span className="block text-[11px] text-muted">{text}</span>
      </span>
    </button>
  );
}
